import React, { FunctionComponent } from 'react';
import { v4 as uuid } from 'uuid';
import { Icon } from '@iag-packages/chroma-react/lib/components';

import { Card } from 'components';
import { Button, Heading, Text } from 'components/chroma';

import { toAussieStandardDateFormat } from 'scenes/common/helpers/datetime';

import {
  Driver,
  MotorDriverSubSection,
  MotorFlowType,
  MotorFullPageProps,
} from 'scenes/motor/types';

const getLicenseDuration = (years: number) => {
  if (years < 1) {
    return 'less than 1 year';
  }
  if (years === 1) {
    return `${years} year`;
  }
  if (years >= 5) {
    return `5+ years`;
  }
  return `${years} years`;
};

const getClaimText = (claimCount: number): string => {
  if (claimCount === 0) {
    return 'No claims made';
  }
  if (claimCount === 1) {
    return `${claimCount} claim made`;
  }
  return `${claimCount} claims made`;
};

const getPayableExcessText = (excessCount: number): string => {
  if (excessCount === 0) {
    return 'No claims were excess payable';
  }
  if (excessCount === 1) {
    return `${excessCount} claim was excess payable`;
  }
  return `${excessCount} claims were excess payable`;
};

export const DriverDetailsCard: FunctionComponent<
  Pick<MotorFullPageProps, 'onEditDriver' | 'onRemoveDriver'> & {
    driver: Driver;
    index: number;
    flowType: MotorFlowType;
  }
> = ({ driver, onEditDriver, onRemoveDriver, index, flowType }) => {
  let HeadingDisplayName = null;
  let params = {};
  if (flowType !== MotorFlowType.QuoteDetails) {
    HeadingDisplayName = `${driver.firstName} ${driver.lastName}`;
  } else {
    HeadingDisplayName = `Additional Driver ${index + 1}`;
    params = { motorSubSection: MotorDriverSubSection.GenderAndDateOfBirth };
  }
  return (
    <Card id={uuid()} center justify tabIndex={-1} role="listitem">
      <div>
        <Heading size={4} margin={{ bottom: 1 }}>
          {HeadingDisplayName}
        </Heading>
        <Text>{`${driver.gender} - ${toAussieStandardDateFormat(
          driver.dob
        )}`}</Text>
        <Text>
          Licence held for&nbsp;
          {getLicenseDuration(driver.yearsDriving)}
        </Text>
        <Text>{getClaimText(driver.claimsMade)}</Text>
        {driver.claimsMade > 0 && (
          <Text>{getPayableExcessText(driver.payableClaims)}</Text>
        )}
        <Button
          link
          onClick={() => onEditDriver({ ...params, index })}
          margin={{ top: 2 }}
        >
          Update driver details
        </Button>
      </div>
      <Button
        ariaLabel="Remove driver"
        tertiary
        onClick={() => onRemoveDriver(index)}
      >
        <Icon name="times" />
      </Button>
    </Card>
  );
};
